import { FleetSensor } from '../types';
import { supabase } from './supabaseClient';

// The browser holds a session token, never the password. Logging in exchanges
// one for the other; every later admin call proves itself with the token, which
// expires on its own and can be revoked server-side.
const TOKEN_KEY = 'admin_token';

export const validateAdminPassword = async (password: string): Promise<boolean> => {
  if (!supabase) return false;
  try {
    // Server-side check via RPC — the password hash lives only in Postgres
    // (admin_config table, no client access), never in a client-bundled env
    // var. Also enforces rate limiting (5 fails/min -> locked out).
    //
    // Returns a session token rather than a bare boolean: the browser needs
    // something to prove itself with on later admin calls, and it must not be
    // the password.
    const { data, error } = await supabase.rpc('admin_login', {
      p_password: password,
    });
    if (error || !data) {
      if (error) console.error('Admin Password Validation Error', error);
      return false;
    }
    sessionStorage.setItem(TOKEN_KEY, data as string);
    return true;
  } catch (e) {
    console.error('Admin Password Validation Error', e);
    return false;
  }
};

export const getAdminToken = (): string | null => sessionStorage.getItem(TOKEN_KEY);

// Sessions last 8 hours server-side, so a long working day ends with every
// admin action failing at the last step with a bare "unauthorized" — which
// reads as "this feature is broken", not "log in again". Any call that comes
// back unauthorised clears the session and tells the app, so the login screen
// appears instead of an error in small print.
const EXPIRY_LISTENERS: (() => void)[] = [];

export const onSessionExpired = (fn: () => void) => {
  EXPIRY_LISTENERS.push(fn);
  return () => {
    const i = EXPIRY_LISTENERS.indexOf(fn);
    if (i >= 0) EXPIRY_LISTENERS.splice(i, 1);
  };
};

const noteAuthError = (error: { message?: string } | null): boolean => {
  if (!error?.message || !/unauthorized/i.test(error.message)) return false;
  sessionStorage.removeItem(TOKEN_KEY);
  EXPIRY_LISTENERS.forEach(fn => fn());
  return true;
};
export const clearAdminToken = async () => {
  const token = getAdminToken();
  sessionStorage.removeItem(TOKEN_KEY);
  // Revoke it server-side too, so logging out actually invalidates the session
  // instead of just forgetting it locally.
  if (supabase && token) await supabase.rpc('admin_logout', { p_token: token });
};

export interface Owner {
  id: string;
  name: string;
  secretLinkId: string | null;
  archivedAt: string | null;
}

export const fetchOwners = async (): Promise<Owner[]> => {
  const token = getAdminToken();
  if (!supabase || !token) return [];

  const { data, error } = await supabase.rpc('admin_list_owners', { p_token: token });
  if (error || !data) {
    noteAuthError(error);
    console.error('Owners Fetch Error', error);
    return [];
  }
  return data.map((r: any) => ({
    id: r.id, name: r.name, secretLinkId: r.secret_link_id, archivedAt: r.archived_at ?? null,
  }));
};

export const createOwner = async (
  name: string,
  pin: string,
): Promise<Owner | null> => {
  const token = getAdminToken();
  if (!supabase || !token) return null;

  const { data, error } = await supabase.rpc('admin_create_owner', {
    p_token: token,
    p_name: name,
    p_pin: pin,
  });

  if (error || !data || data.length === 0) {
    noteAuthError(error);
    console.error('Create Owner Error', error);
    return null;
  }
  const row = data[0];
  return { id: row.id, name: row.name, secretLinkId: row.secret_link_id, archivedAt: null };
};

export const updateSensor = async (
  sensorId: string,
  alias: string,
  userId: string | null,
  location: string | null,
  status: string | null,
  notes: string | null,
): Promise<boolean> => {
  const token = getAdminToken();
  if (!supabase || !token) return false;

  const { data, error } = await supabase.rpc('admin_update_sensor', {
    p_token: token,
    p_sensor_id: sensorId,
    p_alias: alias,
    p_user_id: userId,
    p_location: location,
    p_status: status,
    p_notes: notes,
  });

  if (error) {
    noteAuthError(error);
    console.error('Sensor Update Error', error);
    return false;
  }
  return data === true;
};

// Returns an error message rather than a bare false — the server refuses to
// delete gateways and telemetry identities, and the reason matters.
export const renameOwner = async (userId: string, name: string): Promise<string | null> => {
  const token = getAdminToken();
  if (!supabase || !token) return 'Not signed in.';

  const { error } = await supabase.rpc('admin_rename_owner', {
    p_token: token, p_user_id: userId, p_name: name,
  });
  return error ? error.message : null;
};

export const archiveOwner = async (userId: string, archived: boolean): Promise<string | null> => {
  const token = getAdminToken();
  if (!supabase || !token) return 'Not signed in.';

  const { error } = await supabase.rpc('admin_archive_owner', {
    p_token: token, p_user_id: userId, p_archived: archived,
  });
  return error ? error.message : null;
};

export const deleteSensor = async (sensorId: string): Promise<string | null> => {
  const token = getAdminToken();
  if (!supabase || !token) return 'Not signed in.';

  const { error } = await supabase.rpc('admin_delete_sensor', {
    p_token: token,
    p_sensor_id: sensorId,
  });
  if (error) { noteAuthError(error); return error.message; }
  return null;
};

// Returns an error message rather than a bare false: the common failure is
// "still owns sensors", and the admin needs to be told which it is.
export const deleteOwner = async (userId: string): Promise<string | null> => {
  const token = getAdminToken();
  if (!supabase || !token) return 'Not signed in.';

  const { error } = await supabase.rpc('admin_delete_owner', {
    p_token: token,
    p_user_id: userId,
  });
  if (error) { noteAuthError(error); return error.message; }
  return null;
};

export interface CreatedGatewayRow {
  deviceId: string;
  ingestKey: string;
}

// Creates both rows a hub needs — its readings identity and its '-hub'
// telemetry identity — and returns the generated keys, which are shown once so
// they can be pasted into tools/provision.py. They stay readable in the
// database afterwards, so this is convenience, not a one-time secret.
export const createGateway = async (
  deviceId: string,
  name: string,
  userId: string | null,
): Promise<{ rows: CreatedGatewayRow[] } | { error: string }> => {
  const token = getAdminToken();
  if (!supabase || !token) return { error: 'Not signed in.' };

  const { data, error } = await supabase.rpc('admin_create_gateway', {
    p_token: token,
    p_device_id: deviceId,
    p_name: name,
    p_user_id: userId,
  });

  if (error) { noteAuthError(error); return { error: error.message }; }
  if (!data || data.length === 0) return { error: 'No rows returned.' };

  return {
    rows: data.map((r: any) => ({ deviceId: r.device_id, ingestKey: r.ingest_key })),
  };
};

export const fetchFleetStatus = async (): Promise<FleetSensor[]> => {
  if (!supabase) return [];

  const { data, error } = await supabase
    .from('sensor_fleet_status')
    .select('*')
    .order('last_seen', { ascending: false, nullsFirst: false });

  if (error || !data) {
    console.error('Fleet Status Fetch Error', error);
    return [];
  }

  return data.map((row: any) => ({
    sensorId: row.sensor_id,
    deviceId: row.device_id,
    sensorName: row.sensor_name,
    alias: row.alias,
    type: row.type,
    role: row.role,
    status: row.status,
    notes: row.notes,
    mac: row.mac,
    location: row.location,
    lastSeen: row.last_seen,
    claimedAt: row.claimed_at,
    userId: row.user_id,
    ownerName: row.owner_name,
    ownerSecretLinkId: row.owner_secret_link_id,
    latestReading: row.latest_reading,
    latestReadingAt: row.latest_reading_at,
    messages24h: Number(row.messages_24h) || 0,
    messages7d: Number(row.messages_7d) || 0,
  }));
};

export interface RawReading {
  createdAt: string;
  payload: Record<string, unknown>;
}

// The readings exactly as stored, newest first. The fleet table only ever
// shows the latest one flattened into columns, which hides both the fields no
// column exists for and the shape of what a device is actually sending.
export const fetchRawReadings = async (sensorId: string, limit = 100): Promise<RawReading[]> => {
  if (!supabase) return [];

  const { data, error } = await supabase
    .from('readings')
    .select('created_at, readings')
    .eq('sensor_id', sensorId)
    .order('created_at', { ascending: false })
    .limit(limit);

  if (error || !data) {
    console.error('Raw Readings Fetch Error', error);
    return [];
  }
  return data.map((r: any) => ({ createdAt: r.created_at, payload: r.readings ?? {} }));
};

// Counts when dryRun, deletes otherwise — one function on both paths so the
// number shown in the confirmation is the number that gets removed.
export const deleteReadings = async (
  sensorId: string, fromISO: string, toISO: string, dryRun: boolean,
): Promise<{ count: number } | { error: string }> => {
  const token = getAdminToken();
  if (!supabase || !token) return { error: 'Not signed in.' };

  const { data, error } = await supabase.rpc('admin_delete_readings', {
    p_token: token,
    p_sensor_id: sensorId,
    p_from: fromISO,
    p_to: toISO,
    p_dry_run: dryRun,
  });

  if (error) { noteAuthError(error); return { error: error.message }; }
  return { count: Number(data) || 0 };
};

export interface LoginStats {
  userId: string;
  name: string;
  secretLinkId: string | null;
  totalLogins: number;
  logins30d: number;
  lastLoginAt: string | null;
}

export const fetchLoginStats = async (): Promise<LoginStats[]> => {
  const token = getAdminToken();
  if (!supabase || !token) return [];

  const { data, error } = await supabase.rpc('admin_login_stats', { p_token: token });
  if (error || !data) {
    noteAuthError(error);
    console.error('Login Stats Fetch Error', error);
    return [];
  }
  return data.map((r: any) => ({
    userId: r.user_id,
    name: r.name,
    secretLinkId: r.secret_link_id,
    totalLogins: Number(r.total_logins) || 0,
    logins30d: Number(r.logins_30d) || 0,
    lastLoginAt: r.last_login_at,
  }));
};

export interface VisitStats {
  userId: string;
  totalVisits: number;
  visits30d: number;
  lastVisitAt: string | null;
}

export const fetchVisitStats = async (): Promise<VisitStats[]> => {
  const token = getAdminToken();
  if (!supabase || !token) return [];

  const { data, error } = await supabase.rpc('admin_visit_stats', { p_token: token });
  if (error || !data) {
    noteAuthError(error);
    console.error('Visit Stats Fetch Error', error);
    return [];
  }
  return data.map((r: any) => ({
    userId: r.user_id,
    totalVisits: Number(r.total_visits) || 0,
    visits30d: Number(r.visits_30d) || 0,
    lastVisitAt: r.last_visit_at,
  }));
};
